import { useState, useRef, useEffect } from "react";

/**
 * SplitDateInput — input de data em 3 campos numéricos (DD / MM / AAAA),
 * estilo validade de cartão: digitação direta com auto-avanço de foco,
 * em vez de <input type="date"> (picker nativo penoso pra datas distantes,
 * ex: data de nascimento — no Android/iOS é preciso rolar um wheel ano a
 * ano) ou <select> com 100+ <option> de ano. Ver docs/07-issues.md.
 *
 * value: string "" ou "YYYY-MM-DD". onChange(novoValue): mesma forma
 * ("" enquanto a data estiver incompleta).
 *
 * IMPORTANTE: todo update de estado interno usa a forma funcional
 * (setParts(prev => ...)) e não o `parts` capturado no closure do render.
 * O auto-avanço de foco (monthRef.current.focus()) dispara o blur do campo
 * anterior de forma SÍNCRONA, ainda dentro do próprio handler de onChange —
 * antes do React ter processado o setState desse mesmo onChange. Um onBlur
 * que lesse `parts` do closure pegava o valor de 1 dígito de ANTES da
 * digitação do 2º dígito e sobrescrevia o valor certo (bug: digitar "31"
 * no dia virava "01" ao avançar pro mês). Com forma funcional, cada update
 * encadeia a partir do estado mais recente de verdade, não do closure.
 *
 * Se o `value` puder mudar externamente enquanto o componente segue
 * montado (ex: trocar de registro sendo editado sem fechar o formulário),
 * passe um `key` diferente por registro no componente pai — mais simples
 * e previsível do que tentar ressincronizar o estado interno em todo caso.
 */

function onlyDigits(str, maxLen) {
  return String(str || "").replace(/\D/g, "").slice(0, maxLen);
}

function combine(d, m, y) {
  if (d && m && y && y.length === 4) {
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return "";
}

function clampPart(str, min, max) {
  if (!str) return "";
  const n = parseInt(str, 10);
  if (Number.isNaN(n)) return "";
  return String(Math.min(max, Math.max(min, n))).padStart(2, "0");
}

function parseValue(value) {
  const v = value || "";
  const [y, m, d] = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-") : ["", "", ""];
  return { y: y || "", m: m || "", d: d || "" };
}

export default function SplitDateInput({
  value,
  onChange,
  inputStyle,
  inputClassName,
  dayMonthWidth = 64,
  yearWidth = 88,
  disabled = false,
  maxDate = null, // "YYYY-MM-DD" opcional — limpa o ano se a data digitada ultrapassar
}) {
  const [parts, setParts] = useState(() => parseValue(value));
  const lastEmitted = useRef(combine(parts.d, parts.m, parts.y));
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  // Ressincroniza só quando o value vem de fora (não é o eco do nosso
  // próprio onChange) — ex: perfil carregado via fetch depois do 1º render.
  useEffect(() => {
    const incoming = value || "";
    if (incoming === lastEmitted.current) return;
    setParts(parseValue(incoming));
    lastEmitted.current = incoming;
  }, [value]);

  // Propaga pro pai só depois que o React já assentou o estado interno de
  // verdade (nunca dentro do próprio handler) — ver nota no topo do arquivo.
  useEffect(() => {
    const combined = combine(parts.d, parts.m, parts.y);
    if (combined === lastEmitted.current) return;
    lastEmitted.current = combined;
    onChange(combined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts]);

  const baseStyle = { width: "100%", boxSizing: "border-box", textAlign: "center", minWidth: 0, ...inputStyle };

  // R: os 3 campos tinham flex-shrink:0 (largura fixa) — numa coluna de grid
  // mais estreita que a soma dos 3, isso fazia o campo Ano vazar por cima do
  // próximo campo do form (ex: "Peso" em BodyMeasurementsModal). Com
  // flex-shrink:1 + minWidth:0 no wrapper, os 3 encolhem juntos em vez de
  // transbordar quando o espaço aperta.
  return (
    <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
      <input
        ref={dayRef}
        className={inputClassName}
        style={{ ...baseStyle, flex: `0 1 ${dayMonthWidth}px` }}
        type="text" inputMode="numeric" pattern="[0-9]*"
        maxLength={2} placeholder="DD" autoComplete="bday-day" disabled={disabled}
        value={parts.d}
        onChange={e => {
          const d = onlyDigits(e.target.value, 2);
          setParts(prev => ({ ...prev, d }));
          if (d.length === 2) monthRef.current?.focus();
        }}
        onBlur={() => setParts(prev => ({ ...prev, d: clampPart(prev.d, 1, 31) }))}
      />
      <input
        ref={monthRef}
        className={inputClassName}
        style={{ ...baseStyle, flex: `0 1 ${dayMonthWidth}px` }}
        type="text" inputMode="numeric" pattern="[0-9]*"
        maxLength={2} placeholder="MM" autoComplete="bday-month" disabled={disabled}
        value={parts.m}
        onChange={e => {
          const m = onlyDigits(e.target.value, 2);
          setParts(prev => ({ ...prev, m }));
          if (m.length === 2) yearRef.current?.focus();
        }}
        onKeyDown={e => { if (e.key === "Backspace" && e.target.value === "") dayRef.current?.focus(); }}
        onBlur={() => setParts(prev => ({ ...prev, m: clampPart(prev.m, 1, 12) }))}
      />
      <input
        ref={yearRef}
        className={inputClassName}
        style={{ ...baseStyle, flex: `0 1 ${yearWidth}px` }}
        type="text" inputMode="numeric" pattern="[0-9]*"
        maxLength={4} placeholder="AAAA" autoComplete="bday-year" disabled={disabled}
        value={parts.y}
        onChange={e => {
          const y = onlyDigits(e.target.value, 4);
          setParts(prev => ({ ...prev, y }));
        }}
        onKeyDown={e => { if (e.key === "Backspace" && e.target.value === "") monthRef.current?.focus(); }}
        onBlur={() => {
          if (!maxDate) return;
          setParts(prev => {
            const combined = combine(prev.d, prev.m, prev.y);
            if (combined && combined > maxDate) return { ...prev, y: "" };
            return prev;
          });
        }}
      />
    </div>
  );
}
